import React from 'react';
import {
  Box,
  CardContent,
  Typography,
  TextField,
  CircularProgress,
  Button,
  Chip,
  InputAdornment,
  IconButton,
  Tooltip,
  Avatar,
  Popper,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  PersonOff as PersonOffIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  PersonOutline as PersonOutlineIcon,
  PhoneOutlined as PhoneOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { formatPhoneNumber } from '../../../utils/formatters/text';
import { DropdownCard, ProductItem, CustomerDisplayBox } from '../SalesStep1/SalesStep1.styles';
import SaleInvoiceToggleSection from '../SaleInvoiceToggleSection';
import {
  CRM_ADD_CLIENT_DENIED_TOOLTIP,
  CRM_EDIT_CLIENT_DENIED_TOOLTIP,
  useSalesStep3Customer,
} from './SalesStep3Customer.hooks';
import QuickCustomerDialog from './QuickCustomerDialog';
import SaleStep3SummaryCard from './SaleStep3SummaryCard';

const SalesStep3Customer: React.FC = () => {
  const theme = useTheme();
  const {
    state,
    orderSummary,
    previousStep,
    setHasInvoice,
    canAddClient,
    canEditClient,
    searchTerm,
    isLoading,
    results,
    hasSearched,
    showDropdown,
    anchorEl,
    searchInputRef,
    quickDialogOpen,
    quickDialogMode,
    quickName,
    setQuickName,
    quickCi,
    setQuickCi,
    quickPhone,
    setQuickPhone,
    quickNameError,
    quickCiError,
    quickFormError,
    isQuickSaving,
    quickCreateAck,
    setQuickCreateAck,
    openQuickCreate,
    openQuickEdit,
    closeQuickDialog,
    handleQuickFormSubmit,
    handleSearchChange,
    handleSearchFocus,
    handleSearchBlur,
    handleCustomerSelect,
    canShowDropdown,
    handleContinueWithoutCustomer,
    handleClearSelectedCustomer,
    handleContinueStep4,
    handleResetCustomerSearch,
  } = useSalesStep3Customer();

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gap: { xs: 1.5, md: 2 },
        alignItems: 'start',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' },
        px: { xs: 1.5, md: 2.5 },
        pt: { xs: 2, md: 2.75 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          mt: 0,
          minWidth: 0,
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}
        >
          <SearchIcon color="primary" />
          Buscar cliente
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.25,
            mb: 2,
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              flex: '1 1 280px',
              minWidth: 0,
              position: 'relative',
            }}
          >
            <TextField
              fullWidth
              size="medium"
              placeholder="Buscar por nombre o CI..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              inputRef={searchInputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: isLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={18} />
                  </InputAdornment>
                ) : undefined,
                sx: (theme) => ({
                  borderRadius: 2.5,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                }),
              }}
              sx={(theme) => ({
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.background.paper,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(
                    theme.palette.success.main,
                    theme.palette.mode === 'dark' ? 0.45 : 0.35,
                  ),
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.success.main, 0.55),
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.success.main,
                },
              })}
            />

            <Popper
              open={showDropdown && !!anchorEl && canShowDropdown}
              anchorEl={anchorEl}
              placement="bottom-start"
              style={{ zIndex: theme.zIndex.modal }}
              modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
              disablePortal={false}
            >
              <DropdownCard sx={{ width: anchorEl?.offsetWidth || 300 }}>
                <CardContent sx={{ p: 0 }}>
                  {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : results.length > 0 ? (
                    results.map((customer) => {
                      const isSelected = state.selectedCustomer?.id === customer.id;
                      return (
                        <ProductItem
                          key={customer.id}
                          isInCart={isSelected}
                          onMouseDown={() => handleCustomerSelect(customer)}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  mb: 0.5,
                                  color: isSelected ? 'primary.main' : 'text.primary',
                                }}
                              >
                                {customer.customerName}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mb: 0.35,
                                }}
                              >
                                <BadgeIcon sx={{ fontSize: 14 }} />
                                CI: {customer.identityDocumentNumber?.trim() || 'No registrada'}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mb: 0.35,
                                }}
                              >
                                <EmailIcon sx={{ fontSize: 14 }} />
                                {customer.customerEmail?.trim() || 'Correo no registrado'}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <PhoneOutlinedIcon sx={{ fontSize: 14 }} />
                                {customer.customerPhone?.trim()
                                  ? formatPhoneNumber(customer.customerPhone)
                                  : 'Celular no registrado'}
                              </Typography>
                            </Box>
                            {isSelected ? (
                              <Chip
                                label="Seleccionado"
                                size="small"
                                sx={(theme) => ({
                                  height: 20,
                                  fontSize: '0.7rem',
                                  bgcolor: 'primary.main',
                                  color: theme.palette.primary.contrastText,
                                })}
                              />
                            ) : null}
                          </Box>
                        </ProductItem>
                      );
                    })
                  ) : hasSearched ? (
                    <Box sx={{ textAlign: 'center', p: 3 }}>
                      <SearchIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No se encontraron clientes
                      </Typography>
                    </Box>
                  ) : null}
                </CardContent>
              </DropdownCard>
            </Popper>
          </Box>
          <Button
            variant="outlined"
            onClick={handleResetCustomerSearch}
            disabled={!searchTerm.trim() && !state.selectedCustomer && results.length === 0}
            startIcon={<RefreshIcon fontSize="small" />}
            sx={(theme) => ({
              minWidth: 108,
              borderRadius: 2.5,
              fontWeight: 700,
              px: 2,
              alignSelf: 'center',
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              '&:hover': {
                borderColor: theme.palette.text.secondary,
                backgroundColor: theme.palette.action.hover,
              },
            })}
          >
            Reiniciar
          </Button>
          <Tooltip
            title={canAddClient ? '' : CRM_ADD_CLIENT_DENIED_TOOLTIP}
            disableHoverListener={canAddClient}
          >
            <Box
              component="span"
              sx={{
                display: { xs: 'block', sm: 'inline-block' },
                width: { xs: '100%', sm: 'auto' },
                alignSelf: 'center',
              }}
            >
              <Button
                variant="outlined"
                onClick={openQuickCreate}
                disabled={isLoading || !canAddClient}
                startIcon={<PersonAddIcon fontSize="small" />}
                fullWidth
                sx={(theme) => ({
                  minWidth: { xs: '100%', sm: 160 },
                  borderRadius: 2.5,
                  fontWeight: 700,
                  px: 2,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme.palette.text.secondary,
                    backgroundColor: theme.palette.action.hover,
                  },
                })}
              >
                Registro rápido
              </Button>
            </Box>
          </Tooltip>
        </Box>

        <Typography
          variant="h6"
          sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}
        >
          <PersonOutlineIcon color="primary" />
          Cliente asignado
        </Typography>

        {state.selectedCustomer ? (
          <CustomerDisplayBox
            sx={(theme) => ({
              mt: 0,
              border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.35 : 0.25)}`,
              borderRadius: 2.5,
            })}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, minWidth: 0, flex: 1 }}>
                <Avatar
                  sx={(theme) => ({
                    width: 48,
                    height: 48,
                    bgcolor: theme.palette.success.main,
                    fontWeight: 700,
                    fontSize: '1.1rem',
                  })}
                >
                  {state.selectedCustomer.customerName.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}
                  >
                    {state.selectedCustomer.customerName}
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 0.75 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <BadgeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      CI: {state.selectedCustomer.identityDocumentNumber?.trim() || 'No registrada'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <EmailIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      {state.selectedCustomer.customerEmail?.trim() || 'Correo no registrado'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <PhoneOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      {state.selectedCustomer.customerPhone?.trim()
                        ? formatPhoneNumber(state.selectedCustomer.customerPhone)
                        : 'Celular no registrado'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, flexShrink: 0 }}>
                <Tooltip title={canEditClient ? 'Editar cliente' : CRM_EDIT_CLIENT_DENIED_TOOLTIP}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={openQuickEdit}
                      disabled={!canEditClient || isQuickSaving}
                      aria-label="Editar cliente"
                      sx={(theme) => ({
                        color: 'text.secondary',
                        border: `1px solid ${theme.palette.divider}`,
                        '&:hover': {
                          color: theme.palette.primary.main,
                          borderColor: alpha(theme.palette.primary.main, 0.55),
                          backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        },
                      })}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Quitar cliente seleccionado">
                  <IconButton
                    size="small"
                    onClick={handleClearSelectedCustomer}
                    sx={(theme) => ({
                      color: 'text.secondary',
                      border: `1px solid ${theme.palette.divider}`,
                      '&:hover': {
                        color: theme.palette.error.light,
                        borderColor: alpha(theme.palette.error.main, 0.55),
                        backgroundColor: alpha(theme.palette.error.main, 0.12),
                      },
                    })}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </CustomerDisplayBox>
        ) : (
          <Box
            sx={(theme) => ({
              p: 3,
              borderRadius: 2.5,
              border: `1px dashed ${theme.palette.divider}`,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.04)
                  : alpha(theme.palette.common.black, 0.02),
              textAlign: 'center',
            })}
          >
            <PersonOffIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
              Ningún cliente asignado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Busca un cliente existente o usa registro rápido para asociarlo a la venta.
            </Typography>
          </Box>
        )}

        <SaleInvoiceToggleSection hasInvoice={state.hasInvoice} onChange={setHasInvoice} />
      </Box>

      <SaleStep3SummaryCard
        selectedLocation={state.selectedLocation}
        selectedProducts={state.selectedProducts}
        selectedCustomer={state.selectedCustomer}
        hasInvoice={state.hasInvoice}
        grandTotal={orderSummary.grandTotal}
        isLoading={isLoading}
        onPreviousStep={previousStep}
        onContinueStep4={handleContinueStep4}
        onContinueWithoutCustomer={handleContinueWithoutCustomer}
        disablePreviousStep={state.mode === 'edit' && !state.pricingEditable}
      />

      <QuickCustomerDialog
        open={quickDialogOpen}
        mode={quickDialogMode}
        quickName={quickName}
        quickCi={quickCi}
        quickPhone={quickPhone}
        quickNameError={quickNameError}
        quickCiError={quickCiError}
        quickFormError={quickFormError}
        isQuickSaving={isQuickSaving}
        createAcknowledged={quickCreateAck}
        onCreateAcknowledgedChange={setQuickCreateAck}
        onClose={closeQuickDialog}
        onNameChange={setQuickName}
        onCiChange={setQuickCi}
        onPhoneChange={setQuickPhone}
        onSubmit={handleQuickFormSubmit}
      />
    </Box>
  );
};

export default SalesStep3Customer;
